import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faLocationDot, faTimes, faChevronDown, faBars } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faYoutube, faLinkedin } from '@fortawesome/free-brands-svg-icons';

const NavbarWrapper = styled.div.attrs(props => ({
  'data-scrolled': props.scrolled
}))`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(0, 0, 0, 0.9)' : 'transparent'};
  transition: all 0.3s ease;
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
`;

const TopBar = styled.div`
  border-bottom: 1px solid rgba(243, 243, 243, 0.8);
  border-color: rgba(255, 255, 255, 0.3);
  background: transparent;
  padding: 0.5rem 0;
  color: white;
`;

const TopBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 4rem;
    text-align: center;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 3rem;
  align-self: center;
  a {
    color: white;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const MainNav = styled.nav`
  padding: 1rem 0;
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  img {
    height: 40px;
  }
`;


const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #D8AC56;
  }
  
  &.active {
    color: #D8AC56;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      right: 0;
      height: 2px;
      background: #D8AC56;
      border-radius: 1px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.div.attrs(props => ({
  'data-isopen': props.isOpen
}))`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-100%'};
  width: 60%;
  max-width: 400px;
  height: 100vh;
  background: ${props => props.theme.colors.dark};
  padding: 2rem;
  transition: right 0.3s ease;

  z-index: 1001;
  overflow-y: auto;
  
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MobileNavLink = styled(NavLink)`
  font-size: 1.2rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &.active::after {
    display: none;
  }
  
  &.active {
    background: rgba(216, 172, 86, 0.1);
    padding-left: 1rem;
    border-left: 3px solid #D8AC56;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MobileNavOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);

  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;

`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;


const DonateButton = styled(Link)`
  border: 2px solid #D8AC56;
  border-radius: 25px;
  background: transparent;
  color: white;
  padding: 0.5rem 1.5rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #D8AC56;
    color: ${props => props.theme.colors.dark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(216, 172, 86, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 2rem;
    align-self: center;
    margin-top: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: 2px solid #D8AC56;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.div`
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover, &.active {
    color: #D8AC56;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.colors.dark};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  min-width: 200px;
  z-index: 1002;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-10px'});
  transition: all 0.3s ease;
  margin-top: 0.5rem;
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 1rem 1.5rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(216, 172, 86, 0.1);
    color: #D8AC56;
  }
`;

const MobileDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileDropdownTrigger = styled.div`
  color: white;
  text-decoration: none;
  font-size: 1.2rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileDropdownMenu = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding-left: 1rem;
  margin-top: 0.5rem;
`;

const MobileDropdownItem = styled(Link)`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 1.1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const SkipLink = styled.a`
  position: absolute;
  top: -40px;
  left: 6px;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 1002;
  
  &:focus {
    top: 6px;
  }
`;

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen(false);
  };

  const toggleMobileDropdown = () => setIsMobileDropdownOpen(!isMobileDropdownOpen);

  // Helper function to check if link is active
  const isLinkActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location.pathname]);

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <NavbarWrapper scrolled={scrolled}>
      <TopBar>
        <TopBarContent>
          <ContactInfo>
            <ContactItem
              href="tel:+212777794712"
              aria-label="Call us at +212 777794712"
            >
              <FontAwesomeIcon icon={faPhone} />
              +212 777794712
            </ContactItem>
            <ContactItem
              href="https://maps.google.com/?q=Hay+Hassani,+Casablanca"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Our location: Hay Hassani, Casablanca"
            >
              <FontAwesomeIcon icon={faLocationDot} />
              Hay Hassani, Casablanca
            </ContactItem>
          </ContactInfo>
          <SocialLinks>
            <a
              href="https://www.instagram.com/association.tbh_"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram"
            >
              <FontAwesomeIcon icon={faInstagram} />
            </a>
            {/* <a
              href="https://www.youtube.com/@association.tbh_"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Subscribe to our YouTube channel"
            >
              <FontAwesomeIcon icon={faYoutube} />
            </a> */}
            <a
              href="https://www.linkedin.com/company/association-tbh"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect with us on LinkedIn"
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
          </SocialLinks>
        </TopBarContent>
      </TopBar>

      <MainNav role="navigation" aria-label="Main navigation">
        <NavContent>
          <Logo to="/" aria-label="The Big Heart - Home">
            <img src="/images/logo.png" alt="The Big Heart Logo" width="43" height="40" />
          </Logo>

          <NavLinks role="menubar">
            <NavLink
              to="/"
              className={isLinkActive('/') ? 'active' : ''}
              role="menuitem"
            >
              HOME
            </NavLink>
            <NavLink
              to="/about"
              className={isLinkActive('/about') ? 'active' : ''}
              role="menuitem"
            >
              ABOUT US
            </NavLink>
            <DropdownContainer
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
              role="menuitem"
              aria-haspopup="true"
              aria-expanded={isDropdownOpen}
            >
              <DropdownTrigger
                tabIndex="0"
                onKeyDown={(e) => handleKeyDown(e, () => setIsDropdownOpen(!isDropdownOpen))}
                className={isLinkActive('/events') || isLinkActive('/campaigns') ? 'active' : ''}
              >
                ACTIVITIES
                <FontAwesomeIcon icon={faChevronDown} size="sm" />
              </DropdownTrigger>
              <DropdownMenu isOpen={isDropdownOpen} role="menu">
                <DropdownItem
                  to="/events"
                  role="menuitem"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  EVENTS
                </DropdownItem>
                <DropdownItem
                  to="/actions"
                  role="menuitem"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  ACTIONS
                </DropdownItem>
                <DropdownItem
                  to="/in-the-spotlight"
                  role="menuitem"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  IN THE SPOTLIGHT
                </DropdownItem>
                <DropdownItem
                  to="/project-video"
                  role="menuitem"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  PROJECT VIDEO
                </DropdownItem>
              </DropdownMenu>
            </DropdownContainer>
            <NavLink
              to="/heartbeat-journey"
              className={isLinkActive('/heartbeat-journey') ? 'active' : ''}
              role="menuitem"
            >
              HEARTBEAT JOURNEY
            </NavLink>
            <NavLink
              to="/blog"
              className={isLinkActive('/blog') ? 'active' : ''}
              role="menuitem"
            >
              BLOG
            </NavLink>
            <NavLink
              to="/contact"
              className={isLinkActive('/contact') ? 'active' : ''}
              role="menuitem"
            >
              CONTACT
            </NavLink>
            <NavLink
              to="/join"
              className={isLinkActive('/join') ? 'active' : ''}
              role="menuitem"
            >
              JOIN US
            </NavLink>
            <DonateButton to="/donate">Donate Now</DonateButton>
          </NavLinks>

          <MobileMenuButton
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
          </MobileMenuButton>
        </NavContent>
      </MainNav>

      <MobileNavOverlay
        isOpen={isMobileMenuOpen}
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
      />

      <MobileNav
        isOpen={isMobileMenuOpen}
        id="mobile-menu"
        role="navigation"
        aria-label="Mobile navigation"
      >
        <CloseButton
          onClick={closeMobileMenu}
          aria-label="Close mobile menu"
        >
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>

        <MobileNavLinks role="menu">
          <MobileNavLink
            to="/"
            className={isLinkActive('/') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            HOME
          </MobileNavLink>
          <MobileNavLink
            to="/about"
            className={isLinkActive('/about') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            ABOUT US
          </MobileNavLink>
          <MobileDropdownContainer role="menuitem">
            <MobileDropdownTrigger
              onClick={toggleMobileDropdown}
              aria-expanded={isMobileDropdownOpen}
              aria-haspopup="true"
            >
              ACTIVITIES
              <FontAwesomeIcon
                icon={faChevronDown}
                size="sm"
                style={{
                  transform: isMobileDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </MobileDropdownTrigger>
            <MobileDropdownMenu isOpen={isMobileDropdownOpen} role="menu">
              <MobileDropdownItem
                to="/events"
                onClick={closeMobileMenu}
                role="menuitem"
              >
                EVENTS
              </MobileDropdownItem>
              <MobileDropdownItem
                to="/actions"
                onClick={closeMobileMenu}
                role="menuitem"
              >
                ACTIONS
              </MobileDropdownItem>
              <MobileDropdownItem
                to="/in-the-spotlight"
                onClick={closeMobileMenu}
                role="menuitem"
              >
                IN THE SPOTLIGHT
              </MobileDropdownItem>
              <MobileDropdownItem
                to="/project-video"
                onClick={closeMobileMenu}
                role="menuitem"
              >
                PROJECT VIDEO
              </MobileDropdownItem>
            </MobileDropdownMenu>
          </MobileDropdownContainer>
          <MobileNavLink
            to="/heartbeat-journey"
            className={isLinkActive('/heartbeat-journey') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            HEARTBEAT JOURNEY
          </MobileNavLink>
          <MobileNavLink
            to="/blog"
            className={isLinkActive('/blog') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            BLOG
          </MobileNavLink>
          <MobileNavLink
            to="/contact"
            className={isLinkActive('/contact') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            CONTACT
          </MobileNavLink>
          <MobileNavLink
            to="/join"
            className={isLinkActive('/join') ? 'active' : ''}
            onClick={closeMobileMenu}
            role="menuitem"
          >
            JOIN US
          </MobileNavLink>
        </MobileNavLinks>

        <DonateButton to="/donate" onClick={closeMobileMenu}>
          Donate Now
        </DonateButton>

        <SocialLinks>
          <a
            href="https://www.instagram.com/association.tbh_/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Instagram"
            onClick={closeMobileMenu}
          >
            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <a
            href="https://www.youtube.com/@association.tbh_"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Subscribe to our YouTube channel"
            onClick={closeMobileMenu}
          >
            <FontAwesomeIcon icon={faYoutube} />
          </a>
          <a
            href="https://www.linkedin.com/company/association-tbh"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Connect with us on LinkedIn"
            onClick={closeMobileMenu}
          >
            <FontAwesomeIcon icon={faLinkedin} />
          </a>
        </SocialLinks>
      </MobileNav>
    </NavbarWrapper>
  );
}

export default Navbar;
